<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { deleteWidget } from '@/modules/dashboard/composables/deleteWidget'
import type { IMultipleCountWidget } from '@/modules/dashboard/types/IMultipleCountWidget'
import { ERouteNames } from '@/router/ERouteNames'

interface Props {
  widgetData: IMultipleCountWidget
  currentTarget?: string
  flat?: boolean
  loading?: boolean
  bordered?: boolean
  /** Число обычным body-1, как в результате импорта. */
  plainValue?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  flat: false,
  loading: false,
  bordered: true,
  plainValue: false,
})

const route = useRoute()
const showMenu = ref(false)

// Проверка роута для отображения меню удаления
const isMonitoringRoute = computed(() => route.name === ERouteNames.MONITORING)

// Маппинг цветов и иконок по умолчанию
/**
 * Маппинг атрибутов: иконки и цвета
 * Включает специфичные статусы аудитов и фолбеки
 */
const attributeMap: Record<string, { color: string; icon: string }> = {
  // Успешные
  'Загружено': { color: 'secondary', icon: 'ri-arrow-down-circle-line' },
  'Успешно': { color: 'success', icon: 'ri-checkbox-circle-line' },
  'Ошибки данных': { color: 'error', icon: 'ri-alert-line' },
  'Завершено': { color: 'success', icon: 'ri-checkbox-line' },
  'Устранено': { color: 'success', icon: 'ri-checkbox-line' },
  'Соответствует': { color: 'success', icon: 'ri-checkbox-line' },
  'Отработано': { color: 'success', icon: 'ri-checkbox-circle-line' },

  // В процессе
  'В работе': { color: 'warning', icon: 'ri-time-line' },

  // Ошибки и Ожидание
  'Не устранено': { color: 'error', icon: 'ri-spam-3-line' },
  'Не соответствует': { color: 'error', icon: 'ri-spam-3-line' },
  'Ожидается': { color: 'info', icon: 'ri-spam-3-line' },
  'Ожидает': { color: 'info', icon: 'ri-spam-3-line' },

  // Нейтральный статус
  'Не применим': { color: 'secondary', icon: 'ri-forbid-line' },

  // Для НС (вероятность повторения)
  'Высокая': { color: 'error', icon: 'ri-alert-line' },
  'Выше средней': { color: 'warning', icon: 'ri-alert-line' },
  'Средняя': { color: 'info', icon: 'ri-error-warning-line' },
  'Низкая': { color: 'success', icon: 'ri-shield-check-line' },
  'Практически исключено': { color: 'success', icon: 'ri-shield-line' },

  // Для НС (последствия)
  'Катастрофические': { color: 'error', icon: 'ri-alarm-warning-line' },
  'Значительные': { color: 'error', icon: 'ri-alert-line' },
  'Средние': { color: 'warning', icon: 'ri-error-warning-line' },
  'Легкие': { color: 'info', icon: 'ri-information-line' },
  'Несущественные': { color: 'success', icon: 'ri-checkbox-circle-line' },
}

const items = computed(() => {
  return props.widgetData.data.map(item => {
    const fallback = attributeMap[item.attribute] || { color: 'primary', icon: 'ri-question-line' }
    return {
      ...item,
      displayIcon: item.icon || fallback.icon,
      displayColor: fallback.color,
      customColor: item.color || null,
      customTextColor: item.text_color || null,
    }
  })
})
</script>

<template>
  <VCard :variant="flat ? 'flat' : undefined" :class="[flat ? 'pa-0 bg-transparent' : 'pa-4']">
    <div v-if="!flat" class="text-h5 mb-4 d-flex align-center justify-space-between">
      <div class="d-flex align-center gap-2">
        <div
          v-show="widgetData.show_icon && widgetData.icon"
          class="chart-icon-wrapper"
          :style="{ background: widgetData.icon_background }"
        >
          <VIcon :icon="widgetData.icon" size="sm" />
        </div>
        {{ widgetData.name }}
      </div>

      <VMenu
        v-if="isMonitoringRoute && !currentTarget"
        v-model="showMenu"
        :close-on-content-click="false"
      >
        <template #activator="{ props: menuProps }">
          <VIconBtn
            v-bind="menuProps"
            variant="plain"
            icon="ri-more-2-line"
          />
        </template>

        <VCard>
          <VList>
            <VListItem
              prepend-icon="ri-delete-bin-3-line"
              @click="deleteWidget(widgetData.id)"
            >
              <VListItemTitle>Удалить</VListItemTitle>
            </VListItem>
          </VList>
        </VCard>
      </VMenu>
    </div>

    <!-- Загрузка (скелетоны) -->
    <div v-if="loading" class="d-flex gap-4 flex-wrap w-100">
      <div
        v-for="i in (widgetData.data.length || 4)"
        :key="i"
        class="d-flex align-center gap-3 flex-grow-1 pa-2 bg-transparent"
        :class="{ 'border rounded': bordered }"
        style="min-width: 140px; height: 58px;"
      >
        <VSkeletonLoader
          type="avatar"
          class="bg-transparent pa-0 ma-0"
          style="width: 40px; height: 40px; min-width: 40px; display: flex; align-items: center; justify-content: center;"
        />
        <div class="d-flex flex-column justify-center flex-grow-1 gap-1">
          <VSkeletonLoader
            type="text"
            class="bg-transparent pa-0 ma-0"
            style="width: 60px; height: 12px;"
          />
          <VSkeletonLoader
            type="text"
            class="bg-transparent pa-0 ma-0"
            style="width: 40px; height: 16px;"
          />
        </div>
      </div>
    </div>

    <!-- Основной контент -->
    <div v-else class="d-flex gap-4 flex-wrap">
      <div
        v-for="(item, index) in items"
        :key="index"
        class="d-flex align-center gap-3 flex-grow-1 pa-2"
        :class="{ 'border rounded': bordered }"
      >
        <VAvatar
          v-if="item.customColor"
          rounded
          size="40"
          :style="{
            backgroundColor: item.customColor,
            color: item.customTextColor || undefined,
          }"
        >
          <VIcon
            :icon="item.displayIcon"
            size="24"
            :color="item.customTextColor || undefined"
          />
        </VAvatar>
        <VAvatar
          v-else
          :color="item.displayColor"
          variant="tonal"
          rounded
          size="40"
        >
          <VIcon :icon="item.displayIcon" size="24" />
        </VAvatar>
        <div>
          <div :class="plainValue ? 'body-1-500 multiple-count-label' : 'text-caption text-medium-emphasis'">
            {{ item.attribute }}
          </div>
          <div :class="plainValue ? 'body-1 multiple-count-value' : 'text-h6 font-weight-bold'">
            {{ item.value_text || item.value }}
          </div>
        </div>
      </div>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.multiple-count-label {
  color: rgb(var(--v-theme-text-primary));
  font-feature-settings: 'liga' off, 'clig' off;
}

.multiple-count-value {
  color: rgba(var(--v-theme-text-primary), var(--v-medium-emphasis-opacity));
  font-feature-settings: 'liga' off, 'clig' off;
}
:deep(.v-skeleton-loader) {
  min-height: unset !important;
  background-color: transparent !important;
}

:deep(.v-skeleton-loader__avatar) {
  height: 40px !important;
  width: 40px !important;
  min-width: 40px !important;
  min-height: 40px !important;
  margin: 0 !important;
}

:deep(.v-skeleton-loader__text) {
  margin: 0 !important;
  height: 100% !important;
  width: 100% !important;
}
</style>
